// Utilitários para validação de documentos brasileiros

/**
 * Remove caracteres não numéricos de uma string
 */
export const removeNonNumeric = (value: string): string => {
  return value.replace(/\D/g, '');
};

/**
 * Formata CPF (000.000.000-00)
 */
export const formatCPF = (value: string): string => {
  const numbers = removeNonNumeric(value);
  return numbers
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
};

/**
 * Formata CNPJ (00.000.000/0000-00)
 */
export const formatCNPJ = (value: string): string => {
  const numbers = removeNonNumeric(value);
  return numbers
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
};

/**
 * Formata CEP (00000-000)
 */
export const formatCEP = (value: string): string => {
  const numbers = removeNonNumeric(value);
  return numbers.replace(/(\d{5})(\d{1,3})$/, '$1-$2');
};

/**
 * Detecta automaticamente se é CPF ou CNPJ e formata
 */
export const formatCPFOrCNPJ = (value: string): string => {
  const numbers = removeNonNumeric(value);
  
  if (numbers.length <= 11) {
    return formatCPF(value);
  } else {
    return formatCNPJ(value);
  }
};

/**
 * Valida CPF
 */
export const isValidCPF = (cpf: string): boolean => {
  const numbers = removeNonNumeric(cpf);
  
  if (numbers.length !== 11) return false;
  
  // Verifica se todos os dígitos são iguais
  if (/^(\d)\1{10}$/.test(numbers)) return false;
  
  // Validação do primeiro dígito verificador
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(numbers[i]) * (10 - i);
  }
  let remainder = sum % 11;
  let digit1 = remainder < 2 ? 0 : 11 - remainder;
  
  if (parseInt(numbers[9]) !== digit1) return false;
  
  // Validação do segundo dígito verificador
  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(numbers[i]) * (11 - i);
  }
  remainder = sum % 11;
  let digit2 = remainder < 2 ? 0 : 11 - remainder;
  
  return parseInt(numbers[10]) === digit2;
};

/**
 * Valida CNPJ
 */
export const isValidCNPJ = (cnpj: string): boolean => {
  const numbers = removeNonNumeric(cnpj);
  
  if (numbers.length !== 14) return false;
  
  // Verifica se todos os dígitos são iguais
  if (/^(\d)\1{13}$/.test(numbers)) return false;
  
  // Validação do primeiro dígito verificador
  const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  let sum = 0;
  for (let i = 0; i < 12; i++) {
    sum += parseInt(numbers[i]) * weights1[i];
  }
  let remainder = sum % 11;
  let digit1 = remainder < 2 ? 0 : 11 - remainder;
  
  if (parseInt(numbers[12]) !== digit1) return false;
  
  // Validação do segundo dígito verificador
  const weights2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  sum = 0;
  for (let i = 0; i < 13; i++) {
    sum += parseInt(numbers[i]) * weights2[i];
  }
  remainder = sum % 11;
  let digit2 = remainder < 2 ? 0 : 11 - remainder;
  
  return parseInt(numbers[13]) === digit2;
};

/**
 * Valida CPF ou CNPJ automaticamente
 */
export const isValidCPFOrCNPJ = (document: string): boolean => {
  const numbers = removeNonNumeric(document);
  
  if (numbers.length === 11) {
    return isValidCPF(document);
  } else if (numbers.length === 14) {
    return isValidCNPJ(document);
  }
  
  return false;
};

/**
 * Valida CEP
 */
export const isValidCEP = (cep: string): boolean => {
  const numbers = removeNonNumeric(cep);
  return numbers.length === 8 && /^\d{8}$/.test(numbers);
};

/**
 * Detecta o tipo de documento
 */
export const getDocumentType = (document: string): 'CPF' | 'CNPJ' | 'INVALID' => {
  const numbers = removeNonNumeric(document);
  
  if (numbers.length === 11) {
    return 'CPF';
  } else if (numbers.length === 14) {
    return 'CNPJ';
  }
  
  return 'INVALID';
};